import { PICKS_CAR, PICKS_FOOT, Pick, pickLabel } from "@/features/services";
import { Vehicle } from "@/store/profile";

/**
 * Анкета консультанта (сценарий D2) — целиком на клиенте. Правило одно:
 * не спрашивать то, что человек уже сказал в онбординге.
 */
export type PersonaKind = "name" | "vehicle" | "hours";

export interface PersonaStep {
  kind: PersonaKind;
  ask: string;
  chips?: string[];
  placeholder: string;
  reply: (answer: string) => string;
}

export const vehicleFromAnswer = (v: string): Vehicle => {
  if (/маш|авто|car/i.test(v)) return "car";
  if (/мото/i.test(v)) return "moto";
  return "foot";
};

export const PERSONA: Record<PersonaKind, PersonaStep> = {
  name: {
    kind: "name",
    ask: "Как вас зовут?",
    placeholder: "Напишите имя",
    reply: (n) => `Приятно, ${n.trim() || "коллега"}.`,
  },
  vehicle: {
    kind: "vehicle",
    ask: "На чём вы ездите — машина, мото, велосипед или пешком?",
    chips: ["На машине", "На мото", "Пешком / самокат"],
    placeholder: "Ответьте или нажмите подсказку",
    reply: (v) =>
      vehicleFromAnswer(v) === "car"
        ? "Понял, есть авто — тогда вам открыты и доставка на машине, и Такси."
        : "Понял, машины нет — Такси отсекаю сразу, чтобы не отнимать время.",
  },
  hours: {
    kind: "hours",
    ask: "Сколько часов в неделю рассчитываете выходить?",
    chips: ["До 15 часов", "15–30 часов", "Больше 30"],
    placeholder: "Ответьте или нажмите подсказку",
    reply: () => "Записал. Дальше буду считать только по тому, что вам реально доступно.",
  },
};

export function personaQueue(profile: {
  name: string | null;
  picksChosen: boolean;
}): PersonaKind[] {
  const q: PersonaKind[] = [];
  if (!profile.name) q.push("name");
  if (!profile.picksChosen) q.push("vehicle");
  q.push("hours");
  return q;
}

export function picksForVehicle(vehicle: Vehicle | null): Pick[] {
  return vehicle === "car" ? PICKS_CAR : PICKS_FOOT;
}

export function personaSummary(profile: {
  name: string | null;
  vehicle: Vehicle | null;
  hours: string | null;
  picks: Pick[];
}): string {
  const car = profile.vehicle === "car";
  const parts = [
    profile.name ? `${profile.name}, ` : "",
    car ? "с машиной" : "без машины",
    profile.hours ? `, ${profile.hours.toLowerCase()}` : "",
  ].join("");
  const list = profile.picks.map(pickLabel).join(", ");
  return `Профиль собран. ${parts}.\nСравниваю для вас: ${list}.\n${
    car ? "Такси оставил — оно вам доступно." : "Такси из сравнения убрал."
  } Теперь спрашивайте.`;
}

export function greeting(name: string | null): string {
  const hi = name ? `Привет, ${name}. ` : "Привет. ";
  return `${hi}Могу сравнить сервисы, показать, где сейчас спрос, и разобрать ваш доход. Отвечаю только по данным — если чего-то не знаю, так и скажу.`;
}

export function personaGreeting(profile: {
  name: string | null;
  picksChosen: boolean;
}): string {
  const hi = profile.name ? `Привет, ${profile.name}. ` : "Привет. ";
  const known: string[] = [];
  if (profile.name) known.push("имя");
  if (profile.picksChosen) known.push("тарифы");
  const queue = personaQueue(profile);
  if (known.length) {
    return `${hi}Из знакомства уже знаю ${known.join(" и ")} — ${
      queue.length === 1 ? "уточню одну вещь" : "уточню пару вещей"
    }, чтобы советовать точнее.`;
  }
  return `${hi}Чтобы советовать по делу, а не вслепую, мне нужно кое-что узнать о вас — это пара минут.`;
}

/** Быстрые вопросы под лентой — как в прототипе. */
export const QUICK_QUESTIONS = [
  { kind: "demand", label: "Где высокий спрос?" },
  { kind: "money", label: "Разобрать мой доход" },
  { kind: "pick", label: "Какой сервис выбрать?" },
  { kind: "demand", label: "Когда лучше выйти завтра?" },
  { kind: "money", label: "Почему мало заказов?" },
] as const;
